import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef } from '@angular/core';

export interface ConfirmationConfig {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info' | 'success';
  icon?: string;
  showIcon?: boolean;
  showCancel?: boolean; // Muestra o oculta el botón de cancelar
  closable?: boolean; // Permite cerrar el modal clickeando fuera o con ESC
}

@Component({
  selector: 'app-confirmation-modal',
  templateUrl: './confirmation-modal.component.html',
  styleUrls: ['./confirmation-modal.component.scss']
})
export class ConfirmationModalComponent implements OnInit {
  @Input() isVisible = false;
  @Input() config: ConfirmationConfig = {
    title: '¿Confirmar acción?',
    message: '¿Está seguro de que desea continuar?',
    confirmText: 'Aceptar',
    cancelText: 'Cancelar',
    type: 'warning',
    showIcon: true,
    showCancel: true,
    closable: true
  };

  @Output() confirmed = new EventEmitter<void>();
  @Output() cancelled = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  
  @ViewChild('confirmButton', { static: false }) confirmButton!: ElementRef<HTMLButtonElement>;

  constructor() {}

  ngOnInit(): void {
    // Configuración por defecto si no se proporciona
    this.config = {
      ...{
        title: '¿Confirmar acción?',
        message: '¿Está seguro de que desea continuar?',
        confirmText: 'Aceptar',
        cancelText: 'Cancelar',
        type: 'warning',
        showIcon: true,
        showCancel: true,
        closable: true
      },
      ...this.config
    };
  }

  /**
   * Obtiene el ícono basado en el tipo de modal
   */
  getIcon(): string {
    if (this.config.icon) {
      return this.config.icon;
    }

    switch (this.config.type) {
      case 'danger':
        return 'fa-exclamation-triangle';
      case 'warning':
        return 'fa-exclamation-circle';
      case 'info':
        return 'fa-info-circle';
      case 'success':
        return 'fa-check-circle';
      default:
        return 'fa-question-circle';
    }
  }

  /**
   * Obtiene las clases CSS para el ícono según el tipo
   */
  getIconClasses(): string {
    const baseClasses = 'w-12 h-12 mx-auto mb-4';
    
    switch (this.config.type) {
      case 'danger':
        return `${baseClasses} text-red-600`;
      case 'warning':
        return `${baseClasses} text-yellow-600`;
      case 'info':
        return `${baseClasses} text-blue-600`;
      case 'success':
        return `${baseClasses} text-green-600`;
      default:
        return `${baseClasses} text-gray-600`;
    }
  }

  /**
   * Obtiene las clases CSS para el botón de confirmación según el tipo
   */
  getConfirmButtonClasses(): string {
    const baseClasses = 'w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm transition-colors duration-200';
    
    switch (this.config.type) {
      case 'danger':
        return `${baseClasses} bg-red-600 hover:bg-red-700 focus:ring-red-500`;
      case 'warning':
        return `${baseClasses} bg-yellow-600 hover:bg-yellow-700 focus:ring-yellow-500`;
      case 'info':
        return `${baseClasses} bg-blue-600 hover:bg-blue-700 focus:ring-blue-500`;
      case 'success':
        return `${baseClasses} bg-green-600 hover:bg-green-700 focus:ring-green-500`;
      default:
        return `${baseClasses} bg-gray-600 hover:bg-gray-700 focus:ring-gray-500`;
    }
  }

  /**
   * Maneja la confirmación
   */
  onConfirm(): void {
    console.log('[ConfirmationModal] onConfirm called, emitting confirmed event');
    // Solo emitir el evento, el padre controlará la visibilidad
    this.confirmed.emit();
  }

  /**
   * Maneja la cancelación
   */
  onCancel(): void {
    // Solo emitir el evento, el padre controlará la visibilidad
    this.cancelled.emit();
  }

  /**
   * Cierra el modal (usado para cerrar sin confirmar ni cancelar explícitamente)
   */
  close(): void {
    // Solo emitir el evento, el padre controlará la visibilidad
    this.closed.emit();
  }

  /**
   * Maneja el click en el overlay
   */
  onOverlayClick(event: Event): void {
    if (this.config.closable !== false && event.target === event.currentTarget) {
      this.onCancel();
    }
  }

  /**
   * Maneja las teclas del teclado
   */
  onKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.config.closable !== false) {
      this.onCancel();
    } else if (event.key === 'Enter') {
      this.onConfirm();
    }
  }
}
